import { useState, useEffect } from 'react';
import { api, WebsiteAnalysis } from './lib/api';
import { ResultsView } from './components/ResultsView';
import { LandingPage } from './components/LandingPage';
import { PricingPage } from './components/PricingPage';
import { ComparisonPage } from './components/ComparisonPage';
import { AIModelBenchmark } from './components/AIModelBenchmark';
import { FAQPage } from './components/FAQPage';
import { ContactPage } from './components/ContactPage';
import { SuccessPage } from './components/SuccessPage';
import { CancelPage } from './components/CancelPage';
import { Navbar } from './components/Navbar';
import { useAuth } from './contexts/AuthContext';
import { initGA, trackPageView, trackEvent } from './lib/analytics';

function App() {
  const { canAnalyze, user } = useAuth();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<string | null>(null);
  const [queueInfo, setQueueInfo] = useState<{ position: number; estimatedWaitSeconds: number } | null>(null);
  const [analysis, setAnalysis] = useState<WebsiteAnalysis | null>(null);
  const [error, setError] = useState<string>('');
  const [showPricing, setShowPricing] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [showBenchmark, setShowBenchmark] = useState(false);
  const [showFaq, setShowFaq] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCancel, setShowCancel] = useState(false);

  useEffect(() => {
    if (!import.meta.env.VITE_GA_MEASUREMENT_ID) {
      console.warn('Missing required Google Analytics key: VITE_GA_MEASUREMENT_ID');
    } else {
      initGA();
      trackPageView(window.location.pathname || '/');
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('pricing') === 'true') {
      setShowPricing(true);
      trackPageView('/pricing');
    }
    if (params.get('compare') === 'true') {
      setShowComparison(true);
      trackPageView('/compare');
    }
    if (params.get('benchmark') === 'true') {
      setShowBenchmark(true);
      trackPageView('/benchmark');
    }
    if (params.get('faq') === 'true') {
      setShowFaq(true);
      trackPageView('/faq');
    }
    if (params.get('contact') === 'true') {
      setShowContact(true);
      trackPageView('/contact');
    }
    if (params.get('success') === 'true') {
      setShowSuccess(true);
      setError('');
      setShowPricing(false);
      trackPageView('/success');
    }
    if (params.get('canceled') === 'true') {
      setShowCancel(true);
      setShowPricing(false);
      trackPageView('/canceled');
    }
  }, []);

  const handleAnalyze = async (url: string) => {
    if (user) {
      const { allowed, reason } = canAnalyze();
      if (!allowed) {
        setError(reason || 'Unable to analyze at this time');
        return;
      }
    }

    setIsAnalyzing(true);
    setCurrentPhase(null);
    setQueueInfo(null);
    setError('');
    setAnalysis(null);

    trackEvent('analysis_started', 'analysis', url);

    try {
      const newAnalysis = await api.createAnalysis(url);

      // Trigger the analysis (don't await — it runs server-side until completion;
      // we want to start polling immediately so we can show queued status / position).
      api.analyzeWebsite(url, newAnalysis.id).catch(err => {
        console.error('Analyze request error:', err);
      });

      api.pollAnalysisUntilComplete(newAnalysis.id, (updatedAnalysis) => {
        setCurrentPhase(
          updatedAnalysis.status === 'queued' ? 'queued' : (updatedAnalysis.phase ?? null)
        );
        if (
          updatedAnalysis.status === 'queued' &&
          typeof updatedAnalysis.queuePosition === 'number' &&
          typeof updatedAnalysis.estimatedWaitSeconds === 'number'
        ) {
          setQueueInfo({
            position: updatedAnalysis.queuePosition,
            estimatedWaitSeconds: updatedAnalysis.estimatedWaitSeconds,
          });
        } else {
          setQueueInfo(null);
        }
        if (updatedAnalysis.status === 'completed') {
          setAnalysis(updatedAnalysis);
          setIsAnalyzing(false);
          setCurrentPhase(null);
          setQueueInfo(null);
          trackPageView('/results');
          trackEvent('analysis_completed', 'analysis', url, updatedAnalysis.overallScore || undefined);
        } else if (updatedAnalysis.status === 'failed') {
          const recs = Array.isArray(updatedAnalysis.recommendations) ? updatedAnalysis.recommendations : [];
          const detail = recs.find((r: any) => r && typeof r.message === 'string')?.message;
          const baseMsg = "We couldn't analyze that website. Please check the URL and try again.";
          setError(detail ? `${baseMsg} (${detail})` : baseMsg);
          setAnalysis(null);
          setIsAnalyzing(false);
          setCurrentPhase(null);
          setQueueInfo(null);
          trackEvent('analysis_failed', 'analysis', url);
        }
      }).catch(err => {
        console.error('Polling error:', err);
        setError('Analysis timed out. Please try again.');
        setIsAnalyzing(false);
        setCurrentPhase(null);
        setQueueInfo(null);
      });

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during analysis');
      setIsAnalyzing(false);
      setCurrentPhase(null);
    }
  };

  const handleReset = () => {
    setAnalysis(null);
    setCurrentPhase(null);
    setError('');
    trackPageView('/');
  };

  if (showSuccess) {
    return <SuccessPage />;
  }

  if (showCancel) {
    return <CancelPage />;
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar 
        onLogoClick={() => {
          handleReset();
          setShowPricing(false);
          setShowComparison(false);
          setShowBenchmark(false);
          setShowFaq(false);
          setShowContact(false);
          window.history.pushState({}, '', '/');
        }}
        onCompareClick={() => {
          setShowComparison(true);
          setShowPricing(false);
          setShowBenchmark(false);
          setShowFaq(false);
          setShowContact(false);
          setAnalysis(null);
          setError('');
          window.history.pushState({}, '', '/?compare=true');
          trackPageView('/compare');
        }}
        onBenchmarkClick={() => {
          setShowBenchmark(true);
          setShowPricing(false);
          setShowComparison(false);
          setShowFaq(false);
          setShowContact(false);
          setAnalysis(null);
          setError('');
          window.history.pushState({}, '', '/?benchmark=true');
          trackPageView('/benchmark');
        }}
        onFaqClick={() => {
          setShowFaq(true);
          setShowPricing(false);
          setShowComparison(false);
          setShowBenchmark(false);
          setShowContact(false);
          setAnalysis(null);
          setError('');
          window.history.pushState({}, '', '/?faq=true');
          trackPageView('/faq');
        }}
        onContactClick={() => {
          setShowContact(true);
          setShowPricing(false);
          setShowComparison(false);
          setShowBenchmark(false);
          setShowFaq(false);
          setAnalysis(null);
          setError('');
          window.history.pushState({}, '', '/?contact=true');
          trackPageView('/contact');
        }}
      />

      {showContact ? (
        <ContactPage />
      ) : showFaq ? (
        <FAQPage />
      ) : showBenchmark ? (
        <AIModelBenchmark />
      ) : showComparison ? (
        <ComparisonPage />
      ) : showPricing ? (
        <PricingPage />
      ) : !analysis ? (
        <>
          {error && (
            <div className="pt-6 px-4">
              <div className="container mx-auto max-w-3xl">
                <div className="border border-red-200 bg-red-50 rounded-lg p-4 flex items-start justify-between gap-4">
                  <p className="text-sm text-red-700">{error}</p>
                  <button
                    onClick={() => setError('')}
                    className="text-sm text-red-600 hover:text-red-800 font-medium shrink-0"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}
          <LandingPage onAnalyze={handleAnalyze} isAnalyzing={isAnalyzing} currentPhase={currentPhase} queueInfo={queueInfo} />
        </>
      ) : (
        <div className="py-12 px-4">
          <div className="container mx-auto">
            {error && (
              <div className="mt-6 border border-gray-200 rounded-lg p-4 max-w-3xl mx-auto">
                <p className="text-sm text-gray-700">{error}</p>
              </div>
            )}
            {analysis && <ResultsView analysis={analysis} onReset={handleReset} />}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
